import React, { useEffect, useState } from 'react';

import { SupportTypes } from '@cd3p/core/constants/common';
import { APIHelpRequest, APIHelpRequestType } from '@cd3p/core/types/api';
import { multilineTextField } from '@cd3p/core/utils/fields';
import { FormField, MenuItem, Modal, Typography } from 'components';
import { useTranslation } from 'react-i18next';

import { FieldError, useForm } from 'third-party';

enum FormFields {
  Subject = 'subject',
  Body = 'body',
}

export type FormProps = {
  [FormFields.Subject]: APIHelpRequestType | '';
  [FormFields.Body]: string;
};

const defaultValues: FormProps = {
  [FormFields.Subject]: '',
  [FormFields.Body]: '',
};

type Props = {
  onClose: () => void;
  onSuccess?: (support: APIHelpRequest) => void;
  preselectedSubject?: APIHelpRequestType;
};

export const SupportModal: React.FC<Props> = ({
  onClose,
  onSuccess,
  preselectedSubject,
}) => {
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
  } = useForm<FormProps>({
    mode: 'onChange',
    defaultValues,
  });

  const [isPending, setIsPending] = useState(false);

  const onSubmit = async (data: FormProps) => {
    if (!isValid) {
      return;
    }
    setIsPending(true);
    onSuccess?.({
      subject: data.subject as APIHelpRequestType,
      body: data.body,
    });
    setIsPending(false);
    onClose();
  };

  useEffect(() => {
    if (preselectedSubject) {
      setValue(FormFields.Subject, preselectedSubject);
    }
  }, [preselectedSubject, setValue]);

  return (
    <Modal
      open
      header={t('support.title')}
      confirmLabel={t('support.submit')}
      onConfirm={() => {
        handleSubmit(onSubmit)();
      }}
      isDisabled={isPending}
      isPending={isPending}
      onClose={onClose}
      width="50rem"
      contentWrapperStyle={{ overflow: 'visible' }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormField
          type="select"
          isRequired
          selectOptions={
            // map over the keys of the SupportTypes object
            // and create a MenuItem for each key
            Object.keys(SupportTypes).map(key => (
              <MenuItem key={key} value={key}>
                <Typography variant="body1" fontWeight={600}>
                  {
                    //@ts-ignore
                    t(SupportTypes[key])
                  }
                </Typography>
              </MenuItem>
            ))
          }
          fieldName={FormFields.Subject}
          label={t('support.subject')}
          placeholder={t('support.subject.placeholder')}
          fieldError={errors[FormFields.Subject]}
          requiredErrorMessage={t('common.form.error.emptyField')}
          control={control}
          maxLength={200}
        />
        <FormField
          fieldName={FormFields.Body}
          label={t('support.description')}
          fieldError={errors[FormFields.Body] as FieldError}
          requiredErrorMessage={t('common.form.error.emptyField')}
          placeholder={t('support.description.placeholder')}
          control={control}
          multiline
          rows={6}
          maxLength={multilineTextField.maxLength}
          isDisabled={isPending}
          isRequired
          inputProps={{
            maxLength: multilineTextField.maxLength,
          }}
        />
      </form>
    </Modal>
  );
};
